import styled from '@emotion/styled';
import { useUserHook } from '@zelos/shared/lib/sign.service';
import Head from 'next/head';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import Footer from './footer';
import Gnb from './gnb';
import {
  footerHeight,
  footerTopMargin,
  headerHeight,
} from './layout-styles';
import { ModalContainer } from '@/components/comm/Modal';
import { useClearApplyTryoutAtomWhenSignedOut } from '@/components/pages/tryouts/apply/apply-tryout.model';
import { breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { notoSansKr } from '@/lib/fonts';
import { useSetHackleUser } from '@/lib/hackle.service';
import { useIdentityCallback } from '@/lib/identity-service';
import { ToastBaseContainer } from '@/lib/toast';
import { useSetGaUserId } from '@/lib/use-set-ga-userid';

type LayoutType = 'app' | 'popup' | 'empty';

type LayoutProps = {
  onlyMain?: boolean,
  themeColor?: string,
  layoutType?: LayoutType,
  bodyColor?: string,
  withoutFooterTopMargin?: boolean,
};

const MainStyled = styled.main`
min-height: calc(100vh - ${headerHeight} - ${footerTopMargin} - ${footerHeight});
@media (max-width: ${breakpointSmall}) {
  min-height: 0;
}
`;

const LayoutContainer = styled.div`
.react-loading-skeleton {
  border-radius: 4px;
}
`;

const AppLayout = ({
  children,
  onlyMain,
  bodyColor,
  withoutFooterTopMargin,
}: PropsWithChildren<LayoutProps>) => {
  useClearApplyTryoutAtomWhenSignedOut();
  useIdentityCallback();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    bodyColor
      ? (document.body.style.backgroundColor = bodyColor)
      : (document.body.style.backgroundColor = '');
  }, [bodyColor]);

  return (
    <LayoutContainer className={notoSansKr.className}>
      {!onlyMain && <Gnb />}
      <MainStyled>{children}</MainStyled>
      {!onlyMain && <Footer withoutFooterTopMargin={withoutFooterTopMargin} backgroundColor={bodyColor} />}
      <ToastBaseContainer />
      <ModalContainer />
    </LayoutContainer>
  );
};

const PopupLayout = ({
  children,
  bodyColor,
}: PropsWithChildren<LayoutProps>) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    bodyColor
      ? (document.body.style.backgroundColor = bodyColor)
      : (document.body.style.backgroundColor = '');
  }, [bodyColor]);

  return (
    <LayoutContainer className={notoSansKr.className}>
      <main>{children}</main>
      <ToastBaseContainer />
      <ModalContainer />
    </LayoutContainer>
  );
};

const EmptyLayout = ({
  children,
}: PropsWithChildren<LayoutProps>) => (
  <main>{children}</main>
);

const Layout = ({
  children,
  themeColor = colors.white,
  layoutType = 'app',
  ...props
}: PropsWithChildren<LayoutProps>) => {
  useUserHook();
  useSetGaUserId();
  useSetHackleUser();

  return (
    <>
      <Head>
        <meta name="theme-color" content={themeColor} />
      </Head>
      {layoutType === 'app' && (
        <AppLayout {...props}>{children}</AppLayout>
      )}
      {layoutType === 'empty' && (
        <EmptyLayout {...props}>{children}</EmptyLayout>
      )}
      {layoutType === 'popup' && (
        <PopupLayout {...props}>{children}</PopupLayout>
      )}
    </>
  );
};

export default Layout;
